import { useRef, type CSSProperties, type JSX, type MouseEvent, type PointerEvent } from 'react';
import { useLocale, useT, type Translate } from '../i18n/useT';
import { formatDay } from './format';
import type { DayCell, DayHalf } from './monthGrid';

interface MonthGridProps {
  cells: DayCell[];
  onDayTap: (cell: DayCell) => void;
  /** Called with -1 (previous) or 1 (next) after a horizontal swipe of at least 50 px. */
  onSwipe?: (direction: -1 | 1) => void;
  /** Bookings are still loading: the days are not known to be free yet. */
  busy?: boolean;
}

const SWIPE_MIN = 50;

const typeLabel = (half: DayHalf, t: Translate): string =>
  ({ own: t('own use'), rental: t('rental'), block: t('maintenance') })[half.type];

const stateLabel = (cell: DayCell, t: Translate): string => {
  if (cell.full) {
    return typeLabel(cell.full, t);
  }
  const parts: string[] = [];
  if (cell.leaving) {
    parts.push(t('{0} ends', { 0: typeLabel(cell.leaving, t) }));
  }
  if (cell.arriving) {
    parts.push(t('{0} starts', { 0: typeLabel(cell.arriving, t) }));
  }
  if (cell.locked) {
    parts.push(t('fully booked'));
  }
  return parts.length > 0 ? parts.join(', ') : t('free');
};

/** Defined once in owner.css, shared with the legend swatch. */
const STRIPE = 'var(--ow-own-pattern)';

/** Own halves are `transparent` so the stripe underneath shows: colour plus pattern. */
const HALF_COLOUR = {
  own: 'transparent',
  rental: 'var(--ow-rental)',
  block: 'var(--ow-block)'
} as const;

const halfColour = (h: DayHalf | undefined): string =>
  h === undefined ? 'var(--ow-free)' : HALF_COLOUR[h.type];

/**
 * Changeover days split diagonally: the leaving booking top-left, the arriving one bottom-right.
 * The own-use stripe is the bottom layer; the split sits on top and leaves own halves clear.
 */
const dayStyle = (cell: DayCell): CSSProperties | undefined => {
  if (cell.full !== undefined || (!cell.leaving && !cell.arriving)) {
    return undefined;
  }
  const hasOwn = cell.leaving?.type === 'own' || cell.arriving?.type === 'own';
  const bothOwn = cell.leaving?.type === 'own' && cell.arriving?.type === 'own';
  const layers: string[] = [];
  if (!bothOwn) {
    layers.push(
      `linear-gradient(135deg, ${halfColour(cell.leaving)} 50%, ${halfColour(cell.arriving)} 50%)`
    );
  }
  if (hasOwn) {
    layers.push(STRIPE);
  }
  return { background: layers.join(', ') };
};

const WEEKDAYS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'] as const;

export const LockIcon = (): JSX.Element => (
  <svg className="ow-lock" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path
      d="M4.5 7V5a3.5 3.5 0 0 1 7 0v2h.5a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1zm1.5 0h4V5a2 2 0 0 0-4 0z"
      fill="currentColor"
    />
  </svg>
);

export const MonthGrid = ({
  cells,
  onDayTap,
  onSwipe,
  busy = false
}: MonthGridProps): JSX.Element => {
  const t = useT();
  const locale = useLocale();
  const start = useRef<{ x: number; y: number } | null>(null);
  // A swipe that starts and ends in the same cell is also a click on it; that click is dropped.
  // A swipe across cells produces no click, so the flag is also cleared by the next press and
  // by any key, which activates a day without a pointerdown.
  const swiped = useRef(false);

  const onPointerDown = (event: PointerEvent): void => {
    start.current = { x: event.clientX, y: event.clientY };
    swiped.current = false;
  };
  const onPointerUp = (event: PointerEvent): void => {
    const from = start.current;
    start.current = null;
    if (from === null || onSwipe === undefined) {
      return;
    }
    const dx = event.clientX - from.x;
    const dy = event.clientY - from.y;
    if (Math.abs(dx) >= SWIPE_MIN && Math.abs(dx) > Math.abs(dy)) {
      swiped.current = true;
      onSwipe(dx < 0 ? 1 : -1);
    }
  };
  const onClickCapture = (event: MouseEvent): void => {
    if (swiped.current) {
      swiped.current = false;
      event.stopPropagation();
    }
  };

  return (
    <div
      className="ow-grid"
      role="group"
      aria-label={t('Calendar')}
      aria-busy={busy}
      data-busy={busy}
      style={{ gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={() => {
        start.current = null;
      }}
      onClickCapture={onClickCapture}
      onKeyDownCapture={() => {
        swiped.current = false;
      }}
    >
      {WEEKDAYS.map((d) => (
        <div key={d} className="ow-dow" aria-hidden="true">
          {t(d)}
        </div>
      ))}
      {cells.map((cell) => {
        const type = cell.full?.type;
        const classes = [
          'ow-day',
          type ? `ow-day--${type}` : '',
          cell.locked && !cell.leaving ? 'ow-day--locked' : '',
          cell.inMonth ? '' : 'ow-day--outside',
          cell.isToday ? 'ow-day--today' : ''
        ];
        return (
          <button
            key={cell.date}
            type="button"
            className={classes.filter(Boolean).join(' ')}
            style={dayStyle(cell)}
            disabled={busy}
            aria-current={cell.isToday ? 'date' : undefined}
            aria-label={`${formatDay(cell.date, locale)}, ${stateLabel(cell, t)}`}
            onClick={() => {
              onDayTap(cell);
            }}
          >
            {Number(cell.date.slice(8, 10))}
            {cell.locked && cell.leaving && (
              // The night after a departure: the hatch over the bottom-right half only.
              <span className="ow-day-locked-half" aria-hidden="true" />
            )}
            {cell.locked && <LockIcon />}
          </button>
        );
      })}
    </div>
  );
};
